/**
 * Side nav ("on this page") for the "Extended Blog Post" section.
 *
 * - Builds active/inactive state for the side-nav links based on which
 *   content block is currently in view (scrollspy via IntersectionObserver).
 * - The nav's own scroll box (`.side-nav__scroll`, overflow-y:auto +
 *   max-height) only moves when the ACTIVE item actually changes - and even
 *   then only just far enough to bring that item back into view. It does
 *   NOT track raw page-scroll position tick by tick, so browsing through a
 *   long sub-section's own text (which can easily be taller than the whole
 *   nav list) never drags the still-current item out of view.
 * - Clicking a link opens any collapsed <details> the target lives in
 *   (both the target itself and every ancestor), then smooth-scrolls to it.
 * - Both the scroll-stop point and the sticky sidebar's `top` are offset by
 *   the sticky header's real height (+20px). The header height is read from
 *   the `data-header-height` ATTRIBUTE VALUE (the theme's header component
 *   writes its current rendered height, in px, into that attribute - it can
 *   change on scroll/shrink and doesn't always match the header element's
 *   own `getBoundingClientRect().height`, e.g. while it's mid-transition).
 *   We fall back to measuring the element's box only if the attribute isn't
 *   a usable number.
 * - On top of that, the announcement bar section that sits ABOVE the header
 *   (`.shopify-section.shopify-section-group-group-header.page-announcement`)
 *   is measured directly and added too, since its height isn't reflected in
 *   `data-header-height` at all - without it the offset came out short by
 *   exactly the announcement bar's height.
 *
 * Submenus (items with sub-headings):
 * - Collapsed by default. A [data-nav-toggle] button ("+"/"-" icon) opens
 *   and closes them; the open/closed state lives as an `is-open` class on
 *   the parent [data-nav-parent] <li>, and the submenu height animates via
 *   a CSS grid-template-rows transition (see the section's stylesheet).
 * - Active-state highlighting follows the parent's open/closed state:
 *   - Submenu CLOSED and one of its sub-items is the active section ->
 *     the PARENT link gets `.is-active` (the sub-link itself is hidden).
 *   - Submenu OPEN -> the actual active sub-link gets `.is-active`.
 *   - A parent's own section being active always highlights the parent
 *     link, regardless of open/closed state.
 *
 * Markup contract (see blog-extended-content.liquid):
 *   [data-section-type="blog-extended-content"]   -> section wrapper
 *     [data-side-nav]                             -> <aside> holding the nav
 *       .side-nav__scroll                         -> inner scrollable box
 *         [data-nav-parent]                       -> <li> with a submenu
 *           [data-nav-toggle]                     -> "+"/"-" toggle button
 *           [data-nav-submenu]                    -> collapsible submenu wrapper
 *         [data-side-nav-link][data-target="ID"]  -> one <a> per heading
 *     [data-side-nav-section]#ID                  -> the matching content block
 *
 * Mobile only: [data-side-nav-toggle] is the pull-tab button; clicking it
 * toggles an `is-open` class on the [data-side-nav] element itself, which
 * CSS uses to slide the nav in/out and flip the tab's arrow icon. When the
 * `nav_bar_left` metafield is on, the section also adds a `--reverse`
 * modifier class to the [data-side-nav] element itself (mirroring the one
 * already used on the wrapper), and the stylesheet flips the drawer to
 * slide in from the left instead of the right.
 */
(function () {
	var HEADER_GAP = 20; // px, on top of the header's own height
	var SCROLLSPY_BOTTOM_MARGIN = '-65%'; // keeps only ~one section "active" at a time
	var DESKTOP_MEDIA_QUERY = '(min-width: 990px)'; // matches the CSS breakpoint
	var NAV_SCROLL_PADDING = 12; // px of breathing room kept around the active link

	function debounce(fn, wait) {
		var timeout;
		return function () {
			clearTimeout(timeout);
			timeout = setTimeout(fn, wait);
		};
	}

	// Opens the target itself (if it's a collapsed <details>) and every
	// collapsed <details> ancestor above it, so scrolling to a heading
	// inside a closed accordion (at either level) actually reveals it
	// instead of landing on a zero-height, invisible spot.
	function openAncestorDetails(el) {
		if (el.tagName === 'DETAILS' && !el.open) {
			el.open = true;
		}
		var parent = el.parentElement;
		while (parent) {
			if (parent.tagName === 'DETAILS' && !parent.open) {
				parent.open = true;
			}
			parent = parent.parentElement;
		}
	}

	function initBlogExtendedContentSideNav(container) {
		var nav = container.querySelector('[data-side-nav]');
		if (!nav) return;

		var links = Array.prototype.slice.call(nav.querySelectorAll('[data-side-nav-link]'));
		var sections = Array.prototype.slice.call(container.querySelectorAll('[data-side-nav-section]'));
		var toggle = container.querySelector('[data-side-nav-toggle]');
		var navToggles = Array.prototype.slice.call(nav.querySelectorAll('[data-nav-toggle]'));
		var navParents = Array.prototype.slice.call(nav.querySelectorAll('[data-nav-parent]'));
		var scrollBox = nav.querySelector('.side-nav__scroll');
		if (!links.length || !sections.length) return;

		// Clean up anything left over from a previous init (theme editor re-renders).
		if (container.__blogExtendedContentObserver) {
			container.__blogExtendedContentObserver.disconnect();
		}
		if (container.__blogExtendedContentResizeObserver) {
			container.__blogExtendedContentResizeObserver.disconnect();
		}
		if (container.__blogExtendedContentHeaderAttrObserver) {
			container.__blogExtendedContentHeaderAttrObserver.disconnect();
		}
		if (container.__blogExtendedContentAnnouncementResizeObserver) {
			container.__blogExtendedContentAnnouncementResizeObserver.disconnect();
		}
		if (container.__blogExtendedContentResizeHandler) {
			window.removeEventListener('resize', container.__blogExtendedContentResizeHandler);
		}
		if (container.__blogExtendedContentScrollSyncHandler) {
			window.removeEventListener('scroll', container.__blogExtendedContentScrollSyncHandler);
			container.__blogExtendedContentScrollSyncHandler = null;
		}

		var header = document.querySelector('[data-header-height]');
		// The sticky header group can carry its own announcement bar section
		// ABOVE the header itself - its height isn't part of `data-header-height`
		// at all, so without measuring it too the nav's top offset comes out
		// short by exactly the announcement bar's height.
		var announcementBar = document.querySelector(
			'.shopify-section.shopify-section-group-group-header.page-announcement'
		);
		var scrollspyObserver = null;
		var currentActiveId = null;
		var currentHighlightLink = null;
		var desktopMQ = window.matchMedia(DESKTOP_MEDIA_QUERY);

		var linksById = {};
		links.forEach(function (link) {
			linksById[link.getAttribute('data-target')] = link;
		});

		// The theme's header component keeps its own real, current height (in
		// px) written into the `data-header-height` attribute - this stays
		// correct even while the header is shrinking/growing (sticky states,
		// announcement bar collapsing, etc). Reading the attribute value is
		// what we actually want; measuring the element's own box is only a
		// fallback for markup that merely uses the attribute as a marker.
		// The announcement bar (if present) isn't covered by that attribute at
		// all, so its own rendered height is measured directly and added on top.
		function getHeaderOffset() {
			var headerHeight = 0;

			if (header) {
				var attrHeight = parseFloat(header.getAttribute('data-header-height'));
				headerHeight = !isNaN(attrHeight) ? attrHeight : header.getBoundingClientRect().height;
			}

			var announcementHeight = announcementBar ? announcementBar.getBoundingClientRect().height : 0;

			return Math.round(headerHeight + announcementHeight + HEADER_GAP);
		}

		// Given the id of the section currently in view, works out which
		// link should actually receive `.is-active`: the sub-link itself
		// when its submenu is open, or the parent link when it's closed
		// (since the sub-link is hidden in that state).
		function getHighlightLink(id) {
			var link = linksById[id];
			if (!link) return null;

			if (link.classList.contains('side-nav__sublink')) {
				var parentItem = link.closest('[data-nav-parent]');
				if (parentItem && !parentItem.classList.contains('is-open')) {
					var parentLink = parentItem.querySelector('.side-nav__row .side-nav__link');
					if (parentLink) return parentLink;
				}
			}

			return link;
		}

		// Nudges the nav's own scroll box just far enough to bring the
		// currently highlighted link back inside its visible area - and does
		// nothing at all if that link is already visible. This is what
		// keeps the highlighted item on screen without the nav constantly
		// drifting while the reader is simply scrolling through one
		// section's own (often much longer) text.
		function keepActiveLinkVisible() {
			if (!scrollBox) return;
			if (!desktopMQ.matches && !nav.classList.contains('is-open')) return;
			if (!currentHighlightLink) return;

			var maxScroll = scrollBox.scrollHeight - scrollBox.clientHeight;
			if (maxScroll <= 0) return; // list fits, nothing to scroll

			var boxRect = scrollBox.getBoundingClientRect();
			var linkRect = currentHighlightLink.getBoundingClientRect();
			var linkTopInBox = linkRect.top - boxRect.top;
			var linkBottomInBox = linkRect.bottom - boxRect.top;

			var delta = 0;
			if (linkTopInBox < NAV_SCROLL_PADDING) {
				delta = linkTopInBox - NAV_SCROLL_PADDING;
			} else if (linkBottomInBox > boxRect.height - NAV_SCROLL_PADDING) {
				delta = linkBottomInBox - (boxRect.height - NAV_SCROLL_PADDING);
			}
			if (delta === 0) return; // already comfortably visible - don't move it

			var target = Math.max(0, Math.min(maxScroll, scrollBox.scrollTop + delta));
			if (scrollBox.scrollTo) {
				scrollBox.scrollTo({ top: target, behavior: 'smooth' });
			} else {
				scrollBox.scrollTop = target;
			}
		}

		// Re-applies `.is-active` based on the current active section id and
		// the current open/closed state of every submenu. Called both when
		// the active section changes and when a submenu is toggled (since
		// toggling can move the highlight between a parent and its child
		// without the active section itself changing).
		function applyHighlight() {
			var highlightLink = currentActiveId ? getHighlightLink(currentActiveId) : null;

			links.forEach(function (link) {
				link.classList.toggle('is-active', link === highlightLink);
			});

			currentHighlightLink = highlightLink;
			keepActiveLinkVisible();
		}

		function setActive(id) {
			if (id === currentActiveId) return;
			currentActiveId = id;
			applyHighlight();
		}

		function closeMobileDrawer() {
			nav.classList.remove('is-open');
			if (toggle) toggle.setAttribute('aria-expanded', 'false');
		}

		function openSubmenu(parentItem) {
			parentItem.classList.add('is-open');
			var navToggle = parentItem.querySelector('[data-nav-toggle]');
			if (navToggle) navToggle.setAttribute('aria-expanded', 'true');
		}

		function closeSubmenu(parentItem) {
			parentItem.classList.remove('is-open');
			var navToggle = parentItem.querySelector('[data-nav-toggle]');
			if (navToggle) navToggle.setAttribute('aria-expanded', 'false');
		}

		// Accordion behaviour: only one submenu open at a time.
		function closeOtherSubmenus(exceptItem) {
			navParents.forEach(function (item) {
				if (item !== exceptItem) closeSubmenu(item);
			});
		}

		if (toggle) {
			toggle.addEventListener('click', function () {
				var isOpen = nav.classList.toggle('is-open');
				toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

				// Opening the drawer doesn't itself fire a page 'scroll' event,
				// so catch it up to the current active item right away instead
				// of waiting for the next scroll.
				if (isOpen) {
					keepActiveLinkVisible();
				}
			});
		}

		// Submenu open/close toggles ("+" / "-"). Collapsed by default, and
		// only one submenu may be open at a time - opening one closes any
		// other that was already open.
		navToggles.forEach(function (navToggle) {
			navToggle.addEventListener('click', function () {
				var parentItem = navToggle.closest('[data-nav-parent]');
				if (!parentItem) return;

				var willOpen = !parentItem.classList.contains('is-open');

				if (willOpen) {
					closeOtherSubmenus(parentItem);
					openSubmenu(parentItem);
				} else {
					closeSubmenu(parentItem);
				}

				// Opening/closing a submenu can change which link should be
				// highlighted (parent <-> sub-item) even though the active
				// section hasn't changed.
				applyHighlight();
			});
		});

		function buildScrollspy(offsetPx) {
			if (!('IntersectionObserver' in window)) return;
			if (scrollspyObserver) scrollspyObserver.disconnect();

			scrollspyObserver = new IntersectionObserver(
				function (entries) {
					entries.forEach(function (entry) {
						if (entry.isIntersecting) {
							setActive(entry.target.id);
						}
					});
				},
				{
					root: null,
					// Top edge sits right below the header; bottom edge trims most of
					// the remaining viewport so only ~one section counts as "current".
					rootMargin: '-' + offsetPx + 'px 0px ' + SCROLLSPY_BOTTOM_MARGIN + ' 0px',
					threshold: 0
				}
			);

			sections.forEach(function (section) {
				scrollspyObserver.observe(section);
			});

			container.__blogExtendedContentObserver = scrollspyObserver;
		}

		function applyHeaderOffset() {
			var offset = getHeaderOffset();
			container.style.setProperty('--side-nav-offset', offset + 'px');
			buildScrollspy(offset);
			keepActiveLinkVisible();
		}

		links.forEach(function (link) {
			link.addEventListener('click', function (event) {
				event.preventDefault();
				var targetId = link.getAttribute('data-target');
				var target = document.getElementById(targetId);
				if (!target) return;

				openAncestorDetails(target);

				// If this link lives inside a collapsed submenu, open that
				// submenu too (otherwise its own content stays hidden even
				// though its target section is visible in the article) -
				// and close any other submenu that was open, keeping only
				// one open at a time.
				var parentItem = link.closest('[data-nav-parent]');
				if (parentItem && !parentItem.classList.contains('is-open')) {
					closeOtherSubmenus(parentItem);
					openSubmenu(parentItem);
				}

				var offset = getHeaderOffset();
				var top = target.getBoundingClientRect().top + window.pageYOffset - offset;

				window.scrollTo({ top: top, behavior: 'smooth' });
				setActive(targetId);
				applyHighlight();
				closeMobileDrawer();

				if (window.history && window.history.pushState) {
					window.history.pushState(null, '', '#' + targetId);
				}
			});
		});

		applyHeaderOffset();
		setActive(sections[0].id);

		// Header height can change (responsive breakpoints, a shrink-on-scroll
		// class, an announcement bar collapsing, etc) - keep the offset and
		// scrollspy line in sync with it. The theme's header component is the
		// source of truth for its own height via the `data-header-height`
		// attribute, so we watch that attribute directly (a plain size change
		// of the element's own box, picked up via ResizeObserver, is kept too
		// as a belt-and-braces fallback for headers that don't set it).
		if (header) {
			if ('MutationObserver' in window) {
				var headerAttrObserver = new MutationObserver(function () {
					applyHeaderOffset();
				});
				headerAttrObserver.observe(header, { attributes: true, attributeFilter: ['data-header-height'] });
				container.__blogExtendedContentHeaderAttrObserver = headerAttrObserver;
			}

			if ('ResizeObserver' in window) {
				var resizeObserver = new ResizeObserver(function () {
					applyHeaderOffset();
				});
				resizeObserver.observe(header);
				container.__blogExtendedContentResizeObserver = resizeObserver;
			} else {
				var handler = debounce(applyHeaderOffset, 150);
				window.addEventListener('resize', handler);
				container.__blogExtendedContentResizeHandler = handler;
			}
		} else {
			var fallbackHandler = debounce(applyHeaderOffset, 150);
			window.addEventListener('resize', fallbackHandler);
			container.__blogExtendedContentResizeHandler = fallbackHandler;
		}

		// Same idea for the announcement bar above the header: it can appear,
		// disappear, or change height (a countdown/message rotating, a close
		// button dismissing it) without the header element's own box changing
		// at all, so it needs its own watcher rather than piggybacking on the
		// header's.
		if (announcementBar && 'ResizeObserver' in window) {
			var announcementResizeObserver = new ResizeObserver(function () {
				applyHeaderOffset();
			});
			announcementResizeObserver.observe(announcementBar);
			container.__blogExtendedContentAnnouncementResizeObserver = announcementResizeObserver;
		}
	}

	function initAll(scope) {
		var root = scope && scope.querySelectorAll ? scope : document;
		root.querySelectorAll('[data-section-type="blog-extended-content"]').forEach(function (container) {
			initBlogExtendedContentSideNav(container);
		});
	}

	document.addEventListener('DOMContentLoaded', function () {
		initAll(document);
	});

	// Re-init when the section is added/re-rendered inside the theme editor.
	document.addEventListener('shopify:section:load', function (event) {
		initAll(event.target);
	});
})();